import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  CHROME_EXTENSION_INSTALL_HINT,
  CHROME_WEB_STORE_URL,
  ExtensionBinding,
  getExtensionConfig,
  updateExtensionConfig,
} from "../../../services/extensionSettingsService";
import { getUnits, type Unit } from "../../../services/unitService";

type DraftBinding = {
  unit_id: number | "";
  phone_digits: string;
  is_default: boolean;
};

const emptyBinding = (): DraftBinding => ({
  unit_id: "",
  phone_digits: "",
  is_default: false,
});

const WhatsappExtensionSettingsPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [bindings, setBindings] = useState<DraftBinding[]>([emptyBinding()]);
  const [units, setUnits] = useState<Unit[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, unitsRes] = await Promise.all([
        getExtensionConfig(),
        getUnits({ per_page: 100 }),
      ]);

      const config = configRes.data;
      setEnabled(Boolean(config?.extension_enabled));
      const rows = (config?.bindings || []).map((b: ExtensionBinding) => ({
        unit_id: b.unit_id,
        phone_digits: b.phone_digits,
        is_default: Boolean(b.is_default),
      }));
      setBindings(rows.length > 0 ? rows : [emptyBinding()]);
      setUnits(unitsRes.data || []);
    } catch {
      toast.error(t("settings.whatsapp_extension.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateBinding = (index: number, patch: Partial<DraftBinding>) => {
    setBindings((prev) =>
      prev.map((row, i) => {
        if (i !== index) {
          if (patch.is_default === true) {
            return { ...row, is_default: false };
          }
          return row;
        }
        return { ...row, ...patch };
      })
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payloadBindings = bindings
        .filter((b) => b.unit_id !== "" && b.phone_digits.replace(/\D/g, "").length >= 8)
        .map((b) => ({
          unit_id: Number(b.unit_id),
          phone_digits: b.phone_digits.replace(/\D/g, ""),
          is_default: Boolean(b.is_default),
        }));

      await updateExtensionConfig({
        extension_enabled: enabled,
        bindings: payloadBindings,
      });
      toast.success(t("settings.whatsapp_extension.saveSuccess"));
      await load();
    } catch {
      toast.error(t("settings.whatsapp_extension.saveError"));
    } finally {
      setSaving(false);
    }
  };

  document.title = t("settings.whatsapp_extension.pageTitle");

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("settings.whatsapp_extension.breadcrumb.title")}
          pageTitle={t("settings.whatsapp_extension.breadcrumb.pageTitle")}
        />

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <Row>
            <Col lg={8}>
              <Card>
                <CardHeader>
                  <h5 className="mb-0">{t("settings.whatsapp_extension.card.title")}</h5>
                </CardHeader>
                <CardBody className="d-grid gap-3">
                  <p className="text-muted mb-0">
                    {t("settings.whatsapp_extension.pageHelper")}
                  </p>

                  <Alert color="info" className="mb-0">
                    <strong>
                      {t(
                        CHROME_WEB_STORE_URL
                          ? "settings.whatsapp_extension.install.titleStore"
                          : "settings.whatsapp_extension.install.title"
                      )}
                    </strong>
                    <p className="mb-2 mt-1">
                      {t(
                        CHROME_WEB_STORE_URL
                          ? "settings.whatsapp_extension.install.bodyStore"
                          : "settings.whatsapp_extension.install.body"
                      )}
                    </p>
                    {CHROME_WEB_STORE_URL ? (
                      <a
                        href={CHROME_WEB_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-primary"
                      >
                        {t("settings.whatsapp_extension.install.storeCta")}
                      </a>
                    ) : (
                      <code className="d-block small">
                        {CHROME_EXTENSION_INSTALL_HINT}
                      </code>
                    )}
                  </Alert>

                  <FormGroup switch>
                    <Input
                      type="switch"
                      role="switch"
                      id="extensionEnabled"
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                    />
                    <Label check htmlFor="extensionEnabled">
                      {t("settings.whatsapp_extension.enabledLabel")}
                    </Label>
                    <div className="form-text">
                      {t("settings.whatsapp_extension.enabledHelp")}
                    </div>
                  </FormGroup>

                  <div>
                    <h6 className="mb-2">
                      {t("settings.whatsapp_extension.bindings.title")}
                    </h6>
                    <p className="text-muted small mb-3">
                      {t("settings.whatsapp_extension.bindings.help")}
                    </p>
                    <Table responsive bordered size="sm" className="align-middle mb-2">
                      <thead>
                        <tr>
                          <th>{t("settings.whatsapp_extension.bindings.unit")}</th>
                          <th>{t("settings.whatsapp_extension.bindings.phone")}</th>
                          <th style={{ width: 90 }}>
                            {t("settings.whatsapp_extension.bindings.default")}
                          </th>
                          <th style={{ width: 70 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {bindings.map((row, index) => (
                          <tr key={index}>
                            <td>
                              <Input
                                type="select"
                                value={row.unit_id}
                                onChange={(e) =>
                                  updateBinding(index, {
                                    unit_id:
                                      e.target.value === ""
                                        ? ""
                                        : Number(e.target.value),
                                  })
                                }
                              >
                                <option value="">
                                  {t("settings.whatsapp_extension.bindings.unitPlaceholder")}
                                </option>
                                {units.map((unit) => (
                                  <option key={unit.id} value={unit.id}>
                                    {unit.name}
                                  </option>
                                ))}
                              </Input>
                            </td>
                            <td>
                              <Input
                                type="text"
                                value={row.phone_digits}
                                placeholder="5511999998888"
                                onChange={(e) =>
                                  updateBinding(index, {
                                    phone_digits: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td className="text-center">
                              <Input
                                type="checkbox"
                                checked={row.is_default}
                                onChange={(e) =>
                                  updateBinding(index, {
                                    is_default: e.target.checked,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <Button
                                color="soft-danger"
                                size="sm"
                                type="button"
                                onClick={() =>
                                  setBindings((prev) =>
                                    prev.length === 1
                                      ? [emptyBinding()]
                                      : prev.filter((_, i) => i !== index)
                                  )
                                }
                              >
                                {t("settings.whatsapp_extension.bindings.remove")}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <Button
                      color="light"
                      size="sm"
                      type="button"
                      onClick={() => setBindings((prev) => [...prev, emptyBinding()])}
                    >
                      {t("settings.whatsapp_extension.bindings.add")}
                    </Button>
                  </div>

                  <div>
                    <Button color="primary" disabled={saving} onClick={() => void handleSave()}>
                      {saving ? <Spinner size="sm" /> : t("settings.whatsapp_extension.save")}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col lg={4}>
              <Card>
                <CardBody>
                  <h6>{t("settings.whatsapp_extension.tips.title")}</h6>
                  <ul className="text-muted small mb-0 ps-3">
                    <li>{t("settings.whatsapp_extension.tips.t1")}</li>
                    <li>{t("settings.whatsapp_extension.tips.t2")}</li>
                    <li>{t("settings.whatsapp_extension.tips.t3")}</li>
                  </ul>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
      <ToastContainer />
    </div>
  );
};

export default WhatsappExtensionSettingsPage;
